import React, { useState, useEffect, useMemo } from 'react';
import {
  CustomFormattedDate,
  CustomFormattedMessage,
  useCustomIntl,
} from '../../../../i18n/i18n';
import { MonetaryValue } from '../../../../components/MonetaryValue';
import { TransactionStateLabel } from '../TransactionStateLabel';
import {
  getTransactionPage,
  setPage,
  useTableFilteredTransactions,
  getTransactionFilters,
} from '../../../../state/transactions/transactionFilterSlice';
import {
  getSidebarTransaction,
  selectTransaction,
  unselectTransaction,
} from '../../state/transactionsUiSlice';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import isEqual from 'lodash/isEqual';
import { ServiceRepresentation } from '../../../../components/dataRepresentation/ServiceRepresentation';
import { TableColumn } from '../../../../components/table/TableRow';
import { Table } from '../../../../components/table/Table';
import { Pagination } from '../../../../components/table/Pagination';
import { useVehicles } from '../../../../state/vehicles/useVehicles';
import { mapTransactionsToRows, TransactionRow } from './mapTransactionsToRows';
import { VehicleRepresentation } from '../../../../components/dataRepresentation/VehicleRepresentation';
import { NoDataPlaceholder } from '../../../../components/NoDataPlaceholder';
import { PaymentPartnerRepresentation } from '../../../../components/dataRepresentation/PaymentPartnerRepresentation';
import { MaskedCardNumber } from '../../../../components/MaskedCardNumber';
import { UserManualLink } from '../../../../components/dialog/userManualDialog/UserManualLink';
import { userManualDialogChapters } from '../../../../state/dialogs/chapters';
import { TransactionProductsRepresentation } from '../../../../components/dataRepresentation/TransactionProductsRepresentation';
import classNames from 'classnames';
import { Transaction, weaklyDisplayMonetaryAmounts } from '../../../../models';
import { useTransactionUpdateState } from '../../../../state/transactions/transactionUpdateSlice';

export const TransactionTable: React.FC = () => {
  const intl = useCustomIntl();
  const dispatch = useAppDispatch();
  const { isLoading: isVehiclesLoading, error: errorVehicles } = useVehicles();
  const {
    filteredTransactions,
    totalTransactions,
    isLoading: isTransactionsLoading,
    error: errorTransactions,
  } = useTableFilteredTransactions();

  const { lastRefreshTime } = useTransactionUpdateState();

  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const page = useAppSelector(getTransactionPage);
  const filters = useAppSelector(getTransactionFilters, isEqual);

  useEffect(() => {
    setAllTransactions([]);
  }, [filters, lastRefreshTime]);

  useEffect(() => {
    setAllTransactions((prev) => [...prev, ...filteredTransactions]);
  }, [filteredTransactions]);

  const isLoading = isVehiclesLoading || isTransactionsLoading;
  const error = errorVehicles || errorTransactions;
  const selectedTransaction = useAppSelector(getSidebarTransaction);

  const transactionsById: Record<string, Transaction> = useMemo(() => {
    return allTransactions.reduce(
      (aggregatedResult, transaction) => {
        aggregatedResult[transaction.id] = transaction;
        return aggregatedResult;
      },
      {} as Record<string, Transaction>,
    );
  }, [allTransactions]);

  const handleTransactionClicked = (id: string) => {
    if (selectedTransaction?.id === id) {
      dispatch(unselectTransaction());
    } else {
      dispatch(selectTransaction(transactionsById[id]!));
    }
  };

  const handlePageChanged = () => {
    dispatch(setPage(page + 1));
  };

  const columns: TableColumn<TransactionRow>[] = [
    {
      id: 'vehicle',
      label: intl.formatMessage({
        id: 'transactions.transactionsTable.header.vehicle',
      }),
      renderer: (transaction: TransactionRow) => (
        <VehicleRepresentation
          vehicleType={transaction.vehicle?.type}
          vehicleName={transaction.vehicle?.name}
        />
      ),
    },
    {
      id: 'service',
      label: intl.formatMessage({
        id: 'common.serviceType',
      }),
      renderer: (transaction: TransactionRow) => (
        <ServiceRepresentation serviceType={transaction.service} />
      ),
    },
    {
      id: 'products',
      label: intl.formatMessage({
        id: 'common.transaction.product',
      }),
      renderer: (transaction: TransactionRow) => (
        <TransactionProductsRepresentation products={transaction.products} />
      ),
    },
    {
      id: 'transactionDate',
      label: intl.formatMessage({
        id: 'transactions.transactionsTable.header.transactionDate',
      }),
      renderer: (transaction: TransactionRow) => (
        <CustomFormattedDate value={transaction.transactionDate} />
      ),
    },
    {
      id: 'billingPartner',
      label: intl.formatMessage({
        id: 'common.transaction.paymentPartner',
      }),
      renderer: (transaction: TransactionRow) => (
        <PaymentPartnerRepresentation
          paymentPartner={transaction.billingPartner}
        />
      ),
    },
    {
      id: 'serviceCardLastFourDigits',
      label: intl.formatMessage({
        id: 'common.card',
      }),
      renderer: (transaction: TransactionRow) => (
        <MaskedCardNumber
          lastFourDigits={transaction.serviceCardLastFourDigits}
        />
      ),
    },
    {
      id: 'totalPrice',
      label: (
        <span className="display-flex align-items-center">
          <span className="ellipsis-1">
            <CustomFormattedMessage id="common.transaction.totalPriceNet" />
          </span>
          <UserManualLink
            chapter={userManualDialogChapters.displayTransactions}
            dataTestId="user-manual-link-price-net"
          />
        </span>
      ),
      renderer: (transaction: TransactionRow) => {
        const weaklyDisplay = weaklyDisplayMonetaryAmounts(transaction.state);

        return (
          <span
            className={classNames('text-medium', {
              'text-color-gray': weaklyDisplay,
            })}
          >
            {!!transaction.totalPrice && (
              <MonetaryValue
                amount={parseFloat(transaction.totalPrice.amountNet)}
                currency={transaction.totalPrice.currency}
                strikethrough={weaklyDisplayMonetaryAmounts(transaction.state)}
              />
            )}
          </span>
        );
      },
    },
    {
      id: 'totalPriceConverted',
      label: (
        <span className="display-flex align-items-center">
          <span className="ellipsis-1">
            <CustomFormattedMessage id="common.transaction.totalPriceNetConverted" />
          </span>
          <UserManualLink
            chapter={userManualDialogChapters.displayTransactions}
            dataTestId="user-manual-link-price-net-converted"
          />
        </span>
      ),
      renderer: (transaction: TransactionRow) => {
        const weaklyDisplay = weaklyDisplayMonetaryAmounts(transaction.state);

        return (
          <span
            className={classNames('text-medium', {
              'text-color-gray': weaklyDisplay,
            })}
          >
            {!!transaction.totalPriceConverted && (
              <MonetaryValue
                amount={parseFloat(transaction.totalPriceConverted.amountNet)}
                currency={transaction.totalPriceConverted.currency}
                strikethrough={weaklyDisplay}
              />
            )}
          </span>
        );
      },
    },
    {
      id: 'state',
      label: (
        <span className="display-flex align-items-center">
          <span className="ellipsis-1">
            <CustomFormattedMessage id="transactions.transactionsTable.header.status" />
          </span>
          <UserManualLink
            chapter={userManualDialogChapters.displayTransactions}
            dataTestId="user-manual-link-status"
          />
        </span>
      ),
      renderer: (transaction: TransactionRow) => (
        <TransactionStateLabel state={transaction.state} />
      ),
    },
  ];

  return (
    <>
      <div data-testid="transactions-body-transactions-table">
        {error ? (
          <NoDataPlaceholder />
        ) : (
          <Table
            key="transactions-table"
            columns={columns}
            rowData={mapTransactionsToRows(allTransactions)}
            highlightedRow={selectedTransaction?.id}
            onRowClicked={(key) => {
              handleTransactionClicked(key);
            }}
            isLoading={isLoading}
          />
        )}
      </div>
      {allTransactions.length > 0 && (
        <Pagination
          loadedItems={allTransactions.length}
          totalItems={totalTransactions}
          onLoadMore={handlePageChanged}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
